import { useCallback, useEffect, useRef } from 'react';

import { FabricChannel } from '@atlaskit/analytics-listeners';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	createProductSpecificAnalyticsIntegrationPayload,
	ErrorType,
	Product,
	Severity,
	Team,
} from '@atlassian/growth-commons-error-analytics';

import { type BusinessLogicAnalyticsAttributes, type ErrorHandlerProps } from './types';

const ANALYTICS_CHANNEL = FabricChannel.growth;
const GROWTH_INITIATIVE = 'growth-luna';
const CONFLUENCE_EXPERIMENT_KEY = 'twcg-206_confluence_upgrade_permanent_free';
const JIRA_EXPERIMENT_KEY = 'twcg-206_jira_upgrade_permanent_free'; // needs to be created in Switcheroo

export const useAnalytics = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const sendAnalyticEvent = useCallback(
		(payload: Record<string, any>) => {
			createAnalyticsEvent(payload).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const clickedDismissModal = (
		source: string,
		analyticsAttributes: BusinessLogicAnalyticsAttributes,
	) => {
		sendAnalyticEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'dismissUpgradeFreePoolTargetExperimentModal',
			product: 'growth',
			tags: ['growth'],
			source,
			attributes: {
				growthInitiative: GROWTH_INITIATIVE,
				...analyticsAttributes,
			},
		});
	};

	const clickedPrimaryCTAOnModal = (
		source: string,
		analyticsAttributes: BusinessLogicAnalyticsAttributes,
	) => {
		sendAnalyticEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'upgradeFreePoolTargetExperimentModal',
			product: 'growth',
			tags: ['growth'],
			source,
			attributes: {
				growthInitiative: GROWTH_INITIATIVE,
				...analyticsAttributes,
			},
		});
	};

	const growthErrorHandler = ({ error, errorInfo, product, source }: ErrorHandlerProps) =>
		sendAnalyticEvent(
			createProductSpecificAnalyticsIntegrationPayload({
				team: Team.GROWTH_LUNA,
				product: product === 'confluence' ? Product.CONFLUENCE : Product.JIRA,
				experimentMeta: {
					experimentKey: product === 'confluence' ? CONFLUENCE_EXPERIMENT_KEY : JIRA_EXPERIMENT_KEY,
				},
				errorMessage: error.message,
				errorType: ErrorType.GENERIC,
				source:
					source || (product === 'confluence' ? CONFLUENCE_EXPERIMENT_KEY : JIRA_EXPERIMENT_KEY),
				severity: Severity.HIGH,
				additionalAttributes: {
					...errorInfo,
				},
			}),
		);

	return {
		sendAnalyticEvent,
		clickedDismissModal,
		clickedPrimaryCTAOnModal,
		growthErrorHandler,
	};
};

export const ScreenEvent = ({
	name,
	attributes,
}: {
	attributes?: Record<string, any>;
	name: string;
}) => {
	const { sendAnalyticEvent } = useAnalytics();
	const hasSentScreenEvent = useRef(false);

	useEffect(() => {
		if (hasSentScreenEvent.current) {
			return;
		}
		sendAnalyticEvent({
			eventType: 'screen',
			name,
			attributes,
		});
	}, [sendAnalyticEvent, name, attributes]);

	return null;
};
